import { useEffect } from 'react';

const DEFAULT_OG = 'https://customer-assets.emergentagent.com/job_web-transform-15/artifacts/viz02v6l_fundle_logo-white-min-382x100.png';

/**
 * Centralised <head> manager. Feb 2026 audit rewrite:
 *  - Titles passed through VERBATIM (no auto "Fundle.ai:" prefix).
 *    Pages are responsible for including "| Fundle.ai" in their own title
 *    per the audit's title architecture (point #24).
 *  - `keywords` prop is IGNORED. Google has ignored <meta keywords> for
 *    ranking for years; the giant repeated keyword cloud was actively hurting
 *    per-page relevance. The prop is left in the signature for backward
 *    compatibility so the ~100 existing callers don't need edits.
 *  - `structuredData` accepts a single object OR an array; each block writes
 *    to its own <script data-seo-ld="block-N"> so we can layer Organization
 *    + WebSite + BreadcrumbList + Article + FAQPage.
 */
const SEOHead = ({
  title,
  description,
  canonical,
  ogImage,
  ogType = 'website',
  structuredData,
  noindex = false,
  // eslint-disable-next-line no-unused-vars
  keywords, // Deprecated as of Feb 2026 audit — <meta keywords> no longer emitted.
}) => {
  useEffect(() => {
    const BRAND_FALLBACK = 'Fundle.ai | AI Operating System for Retail & Malls';
    const finalTitle = (title && title.trim()) || BRAND_FALLBACK;
    document.title = finalTitle;

    const upsertMeta = (key, content, attrName) => {
      if (!content) return;
      const selector = `meta[${attrName}="${key}"]`;
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attrName, key);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    const setName = (k, v) => upsertMeta(k, v, 'name');
    const setProp = (k, v) => upsertMeta(k, v, 'property');

    setName('description', description);
    setName('robots', noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large, max-snippet:-1');

    // Feb 2026 audit: HARD-DELETE any <meta name="keywords"> tag that may have
    // been baked into index.html or added by a stale caller. No page should
    // emit meta-keywords going forward.
    document.querySelectorAll('meta[name="keywords"]').forEach((n) => n.remove());

    setProp('og:title', finalTitle);
    setProp('og:description', description);
    setProp('og:type', ogType);
    setProp('og:image', ogImage || DEFAULT_OG);
    setProp('og:site_name', 'Fundle.ai');
    if (canonical) setProp('og:url', canonical);

    setName('twitter:card', 'summary_large_image');
    setName('twitter:title', finalTitle);
    setName('twitter:description', description);
    setName('twitter:image', ogImage || DEFAULT_OG);

    // --- Canonical ---
    if (canonical) {
      let link = document.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      link.setAttribute('href', canonical);
    }

    // --- Structured data (single or array) ---
    document.querySelectorAll('script[data-seo-ld]').forEach((s) => s.remove());

    if (structuredData) {
      const blocks = Array.isArray(structuredData) ? structuredData : [structuredData];
      blocks.forEach((block, idx) => {
        if (!block) return;
        const s = document.createElement('script');
        s.setAttribute('type', 'application/ld+json');
        s.setAttribute('data-seo-ld', `block-${idx}`);
        s.textContent = JSON.stringify(block);
        document.head.appendChild(s);
      });
    }
  }, [title, description, canonical, ogImage, ogType, structuredData, noindex]);

  return null;
};

// --- Reusable schema builders ---

export const orgSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Fundle.ai',
  url: 'https://fundle.ai',
  logo: 'https://customer-assets.emergentagent.com/job_web-transform-15/artifacts/viz02v6l_fundle_logo-white-min-382x100.png',
  description:
    'AI-powered first-party retail data, loyalty, and customer engagement platform for shopping malls, retail brands, and consumer businesses in India.',
  foundingDate: '2024',
  sameAs: [
    'https://www.linkedin.com/company/fundle-ai',
  ],
  contactPoint: [
    {
      '@type': 'ContactPoint',
      contactType: 'sales',
      telephone: '+91-99105-30372',
      email: 'abhinav@fundle.ai',
      areaServed: 'IN',
      availableLanguage: ['en', 'hi'],
    },
  ],
});

export const websiteSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'Fundle.ai',
  url: 'https://fundle.ai',
  potentialAction: {
    '@type': 'SearchAction',
    target: 'https://fundle.ai/search?q={search_term_string}',
    'query-input': 'required name=search_term_string',
  },
});

export const breadcrumbSchema = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((it, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: it.name,
    item: it.url.startsWith('http') ? it.url : `https://fundle.ai${it.url}`,
  })),
});

export const articleSchema = ({ headline, description, datePublished, dateModified, image, url, authorName = 'Fundle.ai Editorial' }) => ({
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline,
  description,
  image: image || DEFAULT_OG,
  datePublished: datePublished || '2026-01-01',
  dateModified: dateModified || datePublished || new Date().toISOString().slice(0, 10),
  author: { '@type': 'Organization', name: authorName },
  publisher: {
    '@type': 'Organization',
    name: 'Fundle.ai',
    logo: { '@type': 'ImageObject', url: DEFAULT_OG },
  },
  mainEntityOfPage: url,
});

export const faqSchema = (faqs) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((f) => ({
    '@type': 'Question',
    name: f.q,
    acceptedAnswer: { '@type': 'Answer', text: f.a },
  })),
});

export const softwareAppSchema = ({ name, description, url, category = 'BusinessApplication' }) => ({
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name,
  description,
  url,
  applicationCategory: category,
  operatingSystem: 'Web, iOS, Android',
  offers: { '@type': 'Offer', priceCurrency: 'INR', price: '0', description: 'Contact for enterprise pricing' },
  publisher: { '@type': 'Organization', name: 'Fundle.ai' },
});

export const serviceSchema = ({ name, description, url, areaServed = 'India' }) => ({
  '@context': 'https://schema.org',
  '@type': 'Service',
  serviceType: name,
  name,
  description,
  url,
  provider: { '@type': 'Organization', name: 'Fundle.ai' },
  areaServed,
});

export default SEOHead;
